import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Loader2,
  Mic,
  MicOff,
  Paperclip,
  Phone,
  PhoneOff,
  X,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';

import { useFieldVoice } from '../api/use-field-voice';
import {
  addFieldReference,
  FIELD_REFERENCE_LIMIT,
  fieldReferenceOptions,
} from '../field-reference-options';

import {
  FieldChatMarkdown,
  FieldConversationDate,
  FieldConversationTime,
} from './field-chat-markdown';
import './field-conversation.css';
import { FieldPersonaAvatar } from './field-persona-avatar';
import { FieldEmpty } from './field-primitives';
import { FIELD_INPUT, FIELD_LABEL } from './field-types';
import { FieldVoiceSession } from './field-voice-session';

import type { FieldPanelProps } from './field-types';
import type { FieldActorId, FieldSnapshot } from '@mio/shared';

interface Props extends FieldPanelProps {
  meetingId: string;
  onLeave?: () => void;
  visible?: boolean;
  focusId?: string | undefined;
  focusRequest?: number;
}

export function FieldMeetingRoom(props: Props) {
  const meeting = props.snapshot.meetings.find((item) => item.id === props.meetingId);
  if (!meeting)
    return (
      <FieldEmpty title="Meeting unavailable">
        Choose a meeting from the company room list.
      </FieldEmpty>
    );
  return <MeetingRoom key={meeting.id} {...props} meeting={meeting} />;
}

function MeetingRoom({
  snapshot,
  busy,
  onAction,
  meeting,
  onLeave,
  visible = true,
  focusId,
  focusRequest,
}: Props & { meeting: FieldSnapshot['meetings'][number] }) {
  const inputId = useId();
  const [speakerId, setSpeakerId] = useState<FieldActorId>(meeting.actorIds[0] ?? 'noor');
  const [draft, setDraft] = useState('');
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [newMessages, setNewMessages] = useState(false);
  const sendInProgress = useRef(false);
  const composing = useRef(false);
  const attachmentDetails = useRef<HTMLDetailsElement>(null);
  const attachmentTrigger = useRef<HTMLElement>(null);
  const history = useRef<HTMLOListElement>(null);
  const composer = useRef<HTMLTextAreaElement>(null);
  const pinned = useRef(true);
  const messages = snapshot.messages.filter((message) => message.meetingId === meeting.id);
  const lastMessage = messages.at(-1);
  const previousMessage = useRef(lastMessage?.id);
  const operationIds = new Set(messages.map((message) => message.operationId));
  const pending = snapshot.operations.some(
    (operation) =>
      operationIds.has(operation.id) && ['pending', 'running'].includes(operation.status),
  );
  const ended = meeting.status === 'ended';
  const disabled =
    !visible ||
    busy ||
    pending ||
    sending ||
    ended ||
    !snapshot.enabled ||
    !snapshot.capabilities.chat ||
    snapshot.engagement.status !== 'active';
  const voice = useFieldVoice(
    speakerId,
    messages,
    (text) =>
      !visible ||
      ended ||
      !snapshot.enabled ||
      !snapshot.capabilities.chat ||
      !snapshot.capabilities.voice ||
      snapshot.engagement.status !== 'active'
        ? Promise.resolve(false)
        : onAction({
            type: 'message',
            actorId: speakerId,
            meetingId: meeting.id,
            text,
            sourceIds,
            modality: 'voice',
          }),
    { awaitingReply: pending, inputBlocked: !visible || busy || sending },
  );
  const stopVoice = voice.stop;
  useEffect(() => {
    if (
      !visible ||
      ended ||
      !snapshot.enabled ||
      !snapshot.capabilities.chat ||
      !snapshot.capabilities.voice ||
      snapshot.engagement.status !== 'active'
    )
      stopVoice();
  }, [
    visible,
    ended,
    snapshot.enabled,
    snapshot.capabilities.chat,
    snapshot.capabilities.voice,
    snapshot.engagement.status,
    stopVoice,
  ]);
  const actorName = (id: FieldActorId) =>
    snapshot.actors.find((actor) => actor.id === id)?.name ?? id;
  const references = fieldReferenceOptions(snapshot);

  useEffect(() => {
    const viewport = history.current;
    const arrived = previousMessage.current !== lastMessage?.id;
    previousMessage.current = lastMessage?.id;
    if (!viewport) return;
    if (pinned.current) {
      if (visible && viewport.clientHeight > 0) viewport.scrollTop = viewport.scrollHeight;
      setNewMessages(false);
    } else if (arrived) setNewMessages(true);
  }, [lastMessage?.id, visible, pending]);

  useEffect(() => {
    const viewport = history.current;
    if (!viewport || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (viewport.clientHeight > 0 && pinned.current) {
        viewport.scrollTop = viewport.scrollHeight;
        setNewMessages(false);
      }
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const input = composer.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = draft ? `${Math.max(48, Math.min(input.scrollHeight, 128))}px` : '40px';
  }, [draft]);

  useEffect(() => {
    if (!visible || !focusId) return;
    pinned.current = false;
    const frame = requestAnimationFrame(() => {
      const message = Array.from(
        history.current?.querySelectorAll<HTMLElement>('[data-message-id]') ?? [],
      ).find((element) => element.dataset.messageId === focusId);
      message?.focus({ preventScroll: true });
      message?.scrollIntoView?.({ block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [visible, focusId, focusRequest]);

  useEffect(() => {
    function dismissAttachment(event: PointerEvent | KeyboardEvent) {
      const details = attachmentDetails.current;
      if (!details?.open) return;
      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Escape') return;
        details.open = false;
        attachmentTrigger.current?.focus();
      } else {
        const target = event.target as HTMLElement;
        if (target.closest('[role="listbox"], .ui-select-positioner')) return;
        if (!details.contains(target)) details.open = false;
      }
    }
    document.addEventListener('pointerdown', dismissAttachment);
    document.addEventListener('keydown', dismissAttachment);
    return () => {
      document.removeEventListener('pointerdown', dismissAttachment);
      document.removeEventListener('keydown', dismissAttachment);
    };
  }, []);

  async function sendMessage() {
    if (disabled || sendInProgress.current || !draft.trim()) return;
    const sentText = draft;
    const sentSources = sourceIds;
    sendInProgress.current = true;
    setSending(true);
    setSendError('');
    try {
      const sent = await onAction({
        type: 'message',
        actorId: speakerId,
        meetingId: meeting.id,
        text: draft.trim(),
        sourceIds,
        modality: 'chat',
      });
      if (sent) {
        setDraft((text) => (text === sentText ? '' : text));
        setSourceIds((current) => (current === sentSources ? [] : current));
        pinned.current = true;
      } else setSendError('Message could not be sent. Your draft is saved; try again.');
    } catch {
      setSendError('Message could not be sent. Your draft is saved; try again.');
    } finally {
      sendInProgress.current = false;
      setSending(false);
    }
  }

  return (
    <section
      className="field-meeting-room flex h-full min-h-0 flex-col overflow-hidden"
      aria-label={`Meeting: ${meeting.title}`}
    >
      <header className="field-meeting-header">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="field-empty-eyebrow">{ended ? 'Meeting ended' : 'Meeting room'}</p>
            <h2 className="field-conversation-title">{meeting.title}</h2>
          </div>
          {onLeave ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                voice.stop();
                onLeave();
              }}
            >
              <ArrowLeft className="size-3.5" aria-hidden="true" />
              Back to conversations
            </Button>
          ) : null}
        </div>
        <p className="field-meeting-attendees">
          You · {meeting.actorIds.map(actorName).join(' · ')}
        </p>
        <p className="text-fg-muted text-xs">
          {ended
            ? 'Meeting ended. The shared transcript and learner notes remain available.'
            : 'Everyone in this room can see the messages and references you share.'}
        </p>
      </header>
      {!ended ? (
        <>
          <div className="field-meeting-toolbar">
            <div className="field-meeting-speaker">
              <label htmlFor={`${inputId}-speaker`} className={FIELD_LABEL}>
                Colleague with the floor
              </label>
              <SelectField
                id={`${inputId}-speaker`}
                value={speakerId}
                disabled={disabled}
                onValueChange={(nextValue) => {
                  const next = meeting.actorIds.find((id) => id === nextValue);
                  if (next) {
                    voice.stop();
                    setSpeakerId(next);
                  }
                }}
                options={[
                  ...(meeting.actorIds.map((id) => ({ value: id, label: actorName(id) })) ?? []),
                ]}
              />
            </div>
            {voice.status === 'off' ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={disabled || !snapshot.capabilities.voice}
                onClick={() => void voice.start()}
              >
                <Phone className="size-4" aria-hidden="true" />
                Speak in room
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={voice.toggleMute}
                  aria-label={voice.muted ? 'Unmute microphone' : 'Mute microphone'}
                  aria-pressed={voice.muted}
                  disabled={voice.status === 'connecting'}
                >
                  {voice.muted ? (
                    <MicOff className="size-4" aria-hidden="true" />
                  ) : (
                    <Mic className="size-4" aria-hidden="true" />
                  )}
                  {voice.muted ? 'Unmute' : 'Mute'}
                </Button>
                <Button variant="secondary" size="sm" onClick={voice.stop}>
                  <PhoneOff className="size-4" aria-hidden="true" />
                  End voice
                </Button>
              </>
            )}
          </div>
          <FieldVoiceSession voice={voice} colleagueName={actorName(speakerId)} />
        </>
      ) : null}
      <div className="field-thread-shell">
        <ol
          ref={history}
          className="field-conversation-history field-message-list"
          aria-label="Shared meeting transcript"
          tabIndex={0}
          onScroll={(event) => {
            const viewport = event.currentTarget;
            if (viewport.clientHeight === 0) return;
            pinned.current =
              viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 72;
            if (pinned.current) setNewMessages(false);
          }}
        >
          {messages.map((message, index) => (
            <li key={message.id} className="min-w-0">
              <FieldConversationDate
                value={message.createdAt}
                previous={messages[index - 1]?.createdAt}
              />
              <article
                className={`field-message field-message--${message.role}`}
                tabIndex={-1}
                data-message-id={message.id}
                data-focused={focusId === message.id}
              >
                {message.role === 'colleague' ? (
                  <FieldPersonaAvatar
                    actorId={message.actorId}
                    name={actorName(message.actorId)}
                    className="field-message-avatar"
                  />
                ) : (
                  <span className="field-message-avatar" aria-hidden="true">
                    {message.role === 'learner' ? 'Y' : '·'}
                  </span>
                )}
                <div className="field-message-body">
                  <div className="field-message-meta">
                    <span className="field-message-author">
                      {message.role === 'learner'
                        ? 'You'
                        : message.role === 'system'
                          ? 'Project update'
                          : actorName(message.actorId)}
                    </span>
                    <FieldConversationTime value={message.createdAt} />
                  </div>
                  {message.role === 'learner' ? (
                    <p className="field-message-text">{message.text}</p>
                  ) : (
                    <FieldChatMarkdown source={message.text} snapshot={snapshot} />
                  )}
                  {message.sourceIds.length ? (
                    <div className="field-message-references" aria-label="Shared references">
                      {message.sourceIds.map((id) => (
                        <span key={id} className="field-reference">
                          <Paperclip className="size-3 shrink-0" aria-hidden="true" />
                          <span>
                            {references.find((reference) => reference.id === id)?.label ?? id}
                          </span>
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </article>
            </li>
          ))}
          {!messages.length && !ended ? (
            <li className="field-conversation-empty">
              <span className="field-empty-eyebrow">Shared conversation</span>
              <h3>Get everyone aligned.</h3>
              <p>
                Share the decision, artifact, or technical question you want to discuss. Choose who
                responds using the floor selector above.
              </p>
              <p>
                Private colleague conversations and your Mio conversation stay outside this room.
              </p>
              <button type="button" disabled={disabled} onClick={() => composer.current?.focus()}>
                Start the conversation <ArrowUp className="size-3.5" aria-hidden="true" />
              </button>
            </li>
          ) : null}
          {pending && voice.status === 'off' ? (
            <li className="field-meeting-pending" role="status">
              <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              <span>{actorName(speakerId)} is responding. The floor opens after this turn.</span>
            </li>
          ) : null}
          {ended ? (
            <li className="field-meeting-saved-notes">
              <h3>Learner meeting notes</h3>
              <p className="field-message-text">{meeting.notes || 'No notes recorded.'}</p>
              <p className="field-message-help">
                These notes do not replace colleague approvals or trusted execution evidence.
              </p>
            </li>
          ) : null}
        </ol>
        {newMessages ? (
          <button
            type="button"
            className="field-new-messages"
            onClick={() => {
              if (history.current) history.current.scrollTop = history.current.scrollHeight;
              pinned.current = true;
              setNewMessages(false);
            }}
          >
            <ArrowDown className="size-3" aria-hidden="true" /> New messages
          </button>
        ) : null}
      </div>
      {!ended ? (
        <div className="field-meeting-composer">
          {voice.error ? <Alert>{voice.error}</Alert> : null}
          {voice.unsent ? (
            <Alert tone="info">
              Speech was not sent: “{voice.unsent}”{' '}
              <button
                type="button"
                className="underline"
                onClick={() => {
                  setDraft((text) => `${text} ${voice.unsent}`.trim());
                  voice.clearUnsent();
                  composer.current?.focus();
                }}
              >
                Move to draft
              </button>
            </Alert>
          ) : null}
          {sendError ? <Alert>{sendError}</Alert> : null}
          <form
            className="field-composer-form"
            onSubmit={(event) => {
              event.preventDefault();
              void sendMessage();
            }}
          >
            <label htmlFor={`${inputId}-message`} className="field-composer-label">
              Message the room; {actorName(speakerId)} responds
            </label>
            <textarea
              ref={composer}
              id={`${inputId}-message`}
              className="field-composer-input"
              rows={2}
              maxLength={12000}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onCompositionStart={() => {
                composing.current = true;
              }}
              onCompositionEnd={() => {
                composing.current = false;
              }}
              aria-describedby={`${inputId}-composer-help`}
              placeholder="Write a message to everyone in the room…"
              onKeyDown={(event) => {
                if (
                  event.key === 'Enter' &&
                  !event.shiftKey &&
                  !event.nativeEvent.isComposing &&
                  !composing.current &&
                  event.keyCode !== 229
                ) {
                  event.preventDefault();
                  if (!disabled && draft.trim()) event.currentTarget.form?.requestSubmit();
                }
              }}
              disabled={disabled}
            />
            {sourceIds.length ? (
              <div
                className="flex max-h-28 flex-wrap gap-1 overflow-y-auto px-3 pb-2"
                aria-label="Attached references"
              >
                {sourceIds.map((id) => {
                  const label = references.find((reference) => reference.id === id)?.label ?? id;
                  return (
                    <span key={id} className="field-reference" title={label}>
                      <Paperclip className="size-3 shrink-0" aria-hidden="true" />
                      <span className="field-reference-label">{label}</span>
                      <button
                        type="button"
                        aria-label={`Remove reference: ${label}`}
                        className="field-reference-remove"
                        disabled={disabled}
                        onClick={() =>
                          setSourceIds((current) => current.filter((reference) => reference !== id))
                        }
                      >
                        <X className="size-3" aria-hidden="true" />
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : null}
            <div className="field-composer-toolbar">
              <details ref={attachmentDetails} className="min-w-0 flex-1">
                <summary
                  ref={attachmentTrigger}
                  className="text-fg-muted cursor-pointer text-xs leading-6"
                >
                  Attach a reference
                  {sourceIds.length
                    ? ` · ${sourceIds.length}/${FIELD_REFERENCE_LIMIT} attached`
                    : ''}
                </summary>
                <label htmlFor={`${inputId}-reference`} className={FIELD_LABEL}>
                  Share a reference with attendees
                </label>
                <SelectField
                  id={`${inputId}-reference`}
                  value=""
                  disabled={disabled || sourceIds.length >= FIELD_REFERENCE_LIMIT}
                  onValueChange={(nextValue) => {
                    if (!nextValue) return;
                    setSourceIds((current) => addFieldReference(current, nextValue));
                  }}
                  options={[
                    { value: '', label: 'Choose another reference' },
                    ...(references.map((reference) => ({
                      value: reference.id,
                      label: reference.label,
                      disabled: sourceIds.includes(reference.id),
                    })) ?? []),
                  ]}
                />
                {sourceIds.length >= FIELD_REFERENCE_LIMIT ? (
                  <p className="text-fg-muted mt-1 text-xs">
                    Remove a reference to attach another.
                  </p>
                ) : null}
              </details>
              <Button type="submit" size="sm" disabled={disabled || !draft.trim()}>
                {sending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                ) : (
                  <ArrowUp className="size-4" aria-hidden="true" />
                )}
                Send to room
              </Button>
            </div>
          </form>
          <p id={`${inputId}-composer-help`} className="field-message-help">
            Enter to send · Shift + Enter for a new line
          </p>
          <details className="field-meeting-notes">
            <summary className="cursor-pointer text-xs">Finish meeting with notes</summary>
            <label htmlFor={`${inputId}-notes`} className={`${FIELD_LABEL} mt-3`}>
              Your notes (shared with attendees)
            </label>
            <textarea
              id={`${inputId}-notes`}
              className={FIELD_INPUT}
              rows={3}
              maxLength={16000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
            <p className="text-fg-muted mt-2 text-xs leading-5">
              Ending the meeting keeps this transcript and shares your notes with attendees. Notes
              do not count as colleague approval.
            </p>
            <Button
              className="mt-2"
              variant="secondary"
              size="sm"
              disabled={
                !visible ||
                busy ||
                pending ||
                sending ||
                !snapshot.enabled ||
                snapshot.engagement.status !== 'active'
              }
              onClick={() => {
                voice.stop();
                void onAction({ type: 'meeting.end', meetingId: meeting.id, notes });
              }}
            >
              End meeting
            </Button>
          </details>
        </div>
      ) : null}
    </section>
  );
}
